/**
 * The filter sidebar.
 *
 * The counts are the substance of the control: each says what will be left
 * if you click it. They arrive from the API already computed with the other
 * groups applied but not their own, so a ticked box does not zero its
 * siblings — see the comment on tenderFacets in api/schema.graphql.
 */

import { useState, type ReactNode } from 'react';
import type { FacetCounts, FacetGroup, FacetSet } from '../facets/types';

const fmt = (value: number | undefined) =>
  (value == null ? '' : value.toLocaleString('bg-BG'));

interface RowProps {
  label: string;
  count?: number | undefined;
  checked: boolean;
  kind?: 'checkbox' | 'radio';
  onToggle: () => void;
  expander?: ReactNode;
  indent?: boolean;
}

function Row({ label, count, checked, kind = 'checkbox', onToggle, expander, indent }: RowProps) {
  return (
    <label className={indent ? 'fct-row fct-row-sub' : 'fct-row'}>
      {expander ?? (indent ? null : <span className="fct-spacer" />)}
      <input type={kind} checked={checked} onChange={onToggle} />
      <span className="fct-name">{label}</span>
      <span className="fct-count">{fmt(count)}</span>
    </label>
  );
}

type AnyState = Record<string, unknown>;

interface SidebarProps<TState extends AnyState> {
  facets: FacetSet<TState, unknown, unknown>;
  state: TState;
  counts: FacetCounts;
  onToggleList: (key: string, value: string) => void;
  onSetValue: (key: string, value: string | boolean) => void;
  onReset: () => void;
  /** Closes the phone bottom sheet; the button is hidden once the panel
      lays out as a sidebar. */
  onClose?: () => void;
}

function listOf(state: AnyState, key: string): string[] {
  const value = state[key];
  return Array.isArray(value) ? (value as string[]) : [];
}

function TaxonomyRows<TState extends AnyState>(
  { group, props, expanded, onExpand }: {
    group: FacetGroup;
    props: SidebarProps<TState>;
    expanded: Set<string>;
    onExpand: (key: string) => void;
  },
) {
  const { facets, state, counts, onToggleList } = props;
  const taxonomy = facets.taxonomy;
  if (!taxonomy) return null;
  const values = counts[group.counts ?? group.key] ?? {};
  const named = new Set<string>();
  const rows: ReactNode[] = [];

  for (const branch of taxonomy.branches) {
    branch.leaves.forEach((leaf) => named.add(leaf));
    const present = branch.leaves.filter((leaf) => values[leaf] != null);
    if (present.length === 0) continue;
    const total = present.reduce((sum, leaf) => sum + (values[leaf] ?? 0), 0);
    const open = expanded.has(branch.key);
    rows.push(
      <Row
        key={branch.key}
        label={branch.label}
        count={total}
        checked={listOf(state, taxonomy.branchKey).includes(branch.key)}
        onToggle={() => onToggleList(taxonomy.branchKey, branch.key)}
        expander={(
          <button
            type="button"
            className={open ? 'fct-expand fct-expand-on' : 'fct-expand'}
            title={open ? 'скрий' : 'покажи кодовете'}
            onClick={(event) => { event.preventDefault(); onExpand(branch.key); }}
          >
            ▸
          </button>
        )}
      />,
    );
    if (!open) continue;
    for (const leaf of present) {
      rows.push(
        <Row
          key={`${branch.key}:${leaf}`}
          label={`${leaf} · ${taxonomy.leafLabels[leaf] ?? 'друго'}`}
          count={values[leaf]}
          checked={listOf(state, group.key).includes(leaf)}
          onToggle={() => onToggleList(group.key, leaf)}
          indent
        />,
      );
    }
  }

  // leaves the taxonomy does not name still have to be reachable
  for (const leaf of Object.keys(values).filter((key) => !named.has(key)).sort()) {
    rows.push(
      <Row
        key={`other:${leaf}`}
        label={`${leaf} · ${taxonomy.leafLabels[leaf] ?? 'друго'}`}
        count={values[leaf]}
        checked={listOf(state, group.key).includes(leaf)}
        onToggle={() => onToggleList(group.key, leaf)}
      />,
    );
  }
  return <>{rows}</>;
}

function GroupRows<TState extends AnyState>(
  { group, props, expanded, onExpand }: {
    group: FacetGroup;
    props: SidebarProps<TState>;
    expanded: Set<string>;
    onExpand: (key: string) => void;
  },
) {
  const { state, counts, onToggleList, onSetValue } = props;

  if (group.kind === 'tree') {
    return <TaxonomyRows group={group} props={props} expanded={expanded} onExpand={onExpand} />;
  }

  if (group.kind === 'radio') {
    const options = group.all
      ? [{ value: '', label: 'Всички' }, ...(group.options ?? [])]
      : (group.options ?? []);
    return (
      <>
        {options.map((option) => (
          <Row
            key={option.value || 'all'}
            kind="radio"
            label={option.label}
            count={option.value ? counts[group.counts ?? '']?.[option.value] : undefined}
            checked={(state[group.key] ?? '') === option.value}
            onToggle={() => onSetValue(group.key, option.value)}
          />
        ))}
      </>
    );
  }

  /* A threshold, not a value: "connected to at least N companies" is the
     shape of the question, so the counts beside the presets are cumulative
     and a free-text box takes anything else. */
  if (group.kind === 'threshold') {
    // a threshold group stores a string; the state map is wider
    const current = (state[group.key] as string | undefined) ?? '';
    return (
      <>
        {(group.options ?? []).map((option) => (
          <Row
            key={option.value}
            kind="radio"
            label={option.label}
            count={counts[group.counts ?? '']?.[option.value]}
            checked={current === option.value}
            onToggle={() => onSetValue(group.key, current === option.value ? '' : option.value)}
          />
        ))}
        <label className="fct-row fct-row-free">
          <span className="fct-spacer" />
          <span className="fct-name">поне</span>
          <input
            className="facet-input fct-number"
            type="number"
            min={1}
            inputMode="numeric"
            value={(group.options ?? []).some((option) => option.value === current) ? '' : current}
            onChange={(event) => onSetValue(group.key, event.target.value.trim())}
          />
        </label>
      </>
    );
  }

  if (group.kind === 'flags') {
    return (
      <>
        {(group.items ?? []).map((flag) => (
          <Row
            key={flag.key}
            label={flag.label}
            count={flag.countsFrom && flag.countValue
              ? counts[flag.countsFrom]?.[flag.countValue]
              : undefined}
            checked={Boolean(state[flag.key])}
            onToggle={() => onSetValue(flag.key, !state[flag.key])}
          />
        ))}
      </>
    );
  }

  /* A check group whose values are known up front lists them itself. The
     counts-derived path below can only offer what the API counted, which
     is right for open vocabularies (procedures, provinces) and wrong for
     a fixed set of predicates — those must appear whether or not anything
     currently matches, or the reader cannot tell an absent filter from an
     empty result. */
  if (group.options) {
    const chosen = listOf(state, group.key);
    return (
      <>
        {group.options.map((option) => (
          <Row
            key={option.value}
            label={option.label}
            count={counts[group.counts ?? group.key]?.[option.value]}
            checked={chosen.includes(option.value)}
            onToggle={() => onToggleList(group.key, option.value)}
          />
        ))}
      </>
    );
  }

  const values = Object.entries(counts[group.counts ?? group.key] ?? {})
    .sort((a, b) => b[1] - a[1]);
  if (values.length === 0) {
    return <span className="fct-empty">няма данни</span>;
  }
  const selected = listOf(state, group.key);
  return (
    <>
      {values.map(([value, count]) => (
        <Row
          key={value}
          label={value}
          count={count}
          checked={selected.includes(value)}
          onToggle={() => onToggleList(group.key, value)}
        />
      ))}
    </>
  );
}

export function FacetSidebar<TState extends AnyState>(props: SidebarProps<TState>) {
  const { facets, state, onReset, onClose } = props;
  // groups that are long or rarely used start closed
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(['contractTypes', 'buyerTypes', 'provinces']));
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const toggle = (setter: typeof setCollapsed) => (key: string) => setter((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const active = facets.activeCount(state);

  return (
    <aside className="facet-panel">
      <div className="facet-panel-head">
        <span className="facet-panel-title">Филтри</span>
        {active > 0 && (
          <button type="button" className="facet-reset" onClick={onReset}>
            изчисти ({active})
          </button>
        )}
        {onClose && (
          <button type="button" className="btn facet-done" onClick={onClose}>
            Готово
          </button>
        )}
      </div>
      {facets.groups.map((group) => {
        const shut = collapsed.has(group.key);
        return (
          <section className="fct-group" key={group.key}>
            <button type="button" className="fct-group-head"
              onClick={() => toggle(setCollapsed)(group.key)}>
              <span>{group.title}</span>
              <span className="fct-toggle">{shut ? '+' : '−'}</span>
            </button>
            {!shut && (
              <div className="fct-rows">
                <GroupRows group={group} props={props} expanded={expanded}
                  onExpand={toggle(setExpanded)} />
              </div>
            )}
          </section>
        );
      })}
    </aside>
  );
}
